import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Dialog } from "@mui/material";
import { Plus, Edit2, Trash2, X, ChevronDown, ChevronUp, Users2, Layers, DollarSign, Eye, Save, Search } from "lucide-react";
import styles from "../hrms.module.scss";
import { APIConstant } from "services/api-constant";
import Popup from "components/minor/popup/popup";
import { useTheme } from "context/ThemeContext";

const ax = () => ({
  headers: { Authorization: `Bearer ${sessionStorage.getItem("platform_token") || ""}` }
});

const fmt = (n) => `₹${Math.round(n||0).toLocaleString("en-IN")}`;

// ── Pagination bar ───────────────────────────────────────────────
// Numbered pagination with ellipsis for large page counts.
// Works off whatever list is passed in (already filtered/searched) —
// caller controls the slicing.
function PaginationBar({ page, totalPages, onChange, totalItems, pageSize, T }) {
  if (totalPages <= 1) return null;

  const delta = 2;
  const pages = [];
  for (let p = 1; p <= totalPages; p++) {
    if (p === 1 || p === totalPages || (p >= page - delta && p <= page + delta)) {
      pages.push(p);
    } else if (pages[pages.length - 1] !== "…") {
      pages.push("…");
    }
  }

  const start = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const end   = Math.min(page * pageSize, totalItems);

  const btnBase = {
    minWidth:30, height:30, padding:"0 8px", borderRadius:7,
    border:`1px solid ${T.border}`, background:"none", color:T.muted,
    cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:12.5, fontWeight:600,
  };

  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
      padding:"14px 22px", borderTop:`1px solid ${T.border}`, flexWrap:"wrap", gap:10 }}>
      <div style={{ fontSize:12, color:T.muted }}>
        Showing <strong style={{ color:T.text }}>{start}</strong>–<strong style={{ color:T.text }}>{end}</strong> of <strong style={{ color:T.text }}>{totalItems}</strong>
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:5 }}>
        <button disabled={page===1} onClick={() => onChange(page - 1)}
          style={{ ...btnBase, opacity: page===1 ? 0.4 : 1, cursor: page===1 ? "not-allowed" : "pointer" }}>
          ‹
        </button>
        {pages.map((p, i) => p === "…" ? (
          <span key={`gap-${i}`} style={{ color:T.dim, fontSize:12, padding:"0 4px" }}>…</span>
        ) : (
          <button key={p} onClick={() => onChange(p)}
            style={{ ...btnBase,
              background: p===page ? T.cyan : "none",
              borderColor: p===page ? T.cyan : T.border,
              color: p===page ? "#fff" : T.muted,
              fontWeight: p===page ? 800 : 600 }}>
            {p}
          </button>
        ))}
        <button disabled={page===totalPages} onClick={() => onChange(page + 1)}
          style={{ ...btnBase, opacity: page===totalPages ? 0.4 : 1, cursor: page===totalPages ? "not-allowed" : "pointer" }}>
          ›
        </button>
      </div>
    </div>
  );
}

// Salary breakdown table
function BreakdownTable({ data, onClose }) {
  const { T } = useTheme();
  if (!data) return null;
  const isEmployer = (k) => k.toLowerCase().includes("employer") || k.toLowerCase().startsWith("er ");
  const empDeds    = Object.entries(data.deductions || {}).filter(([k]) => !isEmployer(k) && k !== "TDS");
  const erConts    = Object.entries(data.deductions || {}).filter(([k]) =>  isEmployer(k));
  const tdsRegimes = data.tds_regimes || {};
  const chosenReg  = data.chosen_regime || "";

  const rows = [
    ...Object.entries(data.earnings || {}).map(([k,v]) => ({ label:k, val:v, type:"earning" })),
    { label:"Gross",                  val:data.gross,                type:"total"     },
    ...empDeds.map(([k,v])           => ({ label:k, val:-v,         type:"deduction" })),
    { label:"Take Home (before tax)", val:data.take_home_before_tax, type:"total"     },
    ...(data.tds_monthly > 0 ? [{ label:`TDS — ${chosenReg || "Regime"}`, val:-data.tds_monthly, type:"deduction" }] : []),
    { label:"Net Salary",             val:data.net_salary,           type:"net"       },
    ...erConts.map(([k,v])           => ({ label:`${k} (Employer)`, val:v, type:"info" })),
    { label:"CTC (Total)",            val:data.cost_to_company,      type:"ctc"       },
  ].filter(r => r.val !== 0);

  const colors = {
    earning:T.text, deduction:T.red, total:T.amber,
    net:T.green, info:T.muted, ctc:T.cyan
  };

  return (
    <div style={{ background:T.card, border:`1px solid ${T.cyan}33`, borderRadius:14, width:500, fontFamily:"Outfit,sans-serif", overflow:"hidden" }}>
      <div style={{ padding:"18px 24px", borderBottom:`1px solid ${T.border}`, background:`${T.cyan}0A`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          <h3 style={{ margin:0, fontSize:16, fontWeight:600, color:T.text }}>Salary Breakdown</h3>
          <div style={{ fontSize:12, color:T.muted, marginTop:3 }}>{data.structure_name} · Monthly CTC: {fmt(data.monthly_ctc)}</div>
        </div>
        {onClose && <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:T.muted }}><X size={16}/></button>}
      </div>
      <div style={{ padding:"0 0 16px" }}>
        {rows.map(({ label, val, type }, i) => {
          const isTotal = type === "total" || type === "net" || type === "ctc";
          return (
            <div key={i} style={{
              display:"flex", justifyContent:"space-between", alignItems:"center",
              padding: isTotal ? "10px 24px" : "8px 24px",
              background: type === "net" ? `${T.green}0F` : type === "ctc" ? `${T.cyan}0F` : type === "total" ? `${T.amber}0A` : "transparent",
              borderBottom: isTotal ? `1px solid ${T.border}` : `1px solid ${T.toggle_bg}`,
              borderTop: isTotal ? `1px solid ${T.border}` : "none",
            }}>
              <span style={{ fontSize: isTotal ? 13 : 12.5, fontWeight: isTotal ? 700 : 400, color: isTotal ? T.text : T.muted }}>
                {label}
              </span>
              <span style={{ fontSize: isTotal ? 14 : 13, fontWeight: isTotal ? 800 : 600, fontFamily:"JetBrains Mono,monospace", color: colors[type] }}>
                {val < 0 ? `- ${fmt(-val)}` : fmt(val)}
              </span>
            </div>
          );
        })}
      </div>
      {/* Tax Regime Comparison */}
      {Object.keys(tdsRegimes).length > 1 && (
        <div style={{ padding:"12px 24px 16px", borderTop:`1px solid ${T.border}` }}>
          <div style={{ fontSize:10.5, color:T.muted, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:10 }}>
            Tax Regime Comparison
          </div>
          <div style={{ display:"grid", gridTemplateColumns:`repeat(${Object.keys(tdsRegimes).length}, 1fr)`, gap:10 }}>
            {Object.entries(tdsRegimes).map(([name, info]) => {
              const isChosen = name === chosenReg;
              return (
                <div key={name} style={{
                  padding:"10px 14px", borderRadius:9,
                  background: isChosen ? `${T.cyan}14` : T.toggle_bg,
                  border: `1px solid ${isChosen ? `${T.cyan}40` : T.toggle_bg}`,
                }}>
                  <div style={{ display:"flex", alignItems:"center", gap:5, marginBottom:6 }}>
                    <span style={{ fontSize:12, fontWeight:700, color: isChosen ? T.cyan : T.muted }}>{name}</span>
                    {isChosen && <span style={{ fontSize:9, padding:"1px 6px", borderRadius:6, background:`${T.cyan}26`, color:T.cyan }}>Employee choice</span>}
                  </div>
                  <div style={{ fontSize:11, color:T.dim, marginBottom:2 }}>Taxable Income</div>
                  <div style={{ fontSize:12, fontFamily:"JetBrains Mono,monospace", color:T.amber }}>₹{Math.round(info.taxable_income).toLocaleString("en-IN")}</div>
                  <div style={{ fontSize:11, color:T.dim, marginTop:5, marginBottom:2 }}>Annual Tax</div>
                  <div style={{ fontSize:12, fontFamily:"JetBrains Mono,monospace", color:T.red }}>₹{Math.round(info.annual_tax).toLocaleString("en-IN")}</div>
                  <div style={{ fontSize:11, color:T.dim, marginTop:5, marginBottom:2 }}>Monthly TDS</div>
                  <div style={{ fontSize:14, fontWeight:800, fontFamily:"JetBrains Mono,monospace", color: isChosen ? T.green : T.muted }}>₹{Math.round(info.monthly_tds).toLocaleString("en-IN")}</div>
                </div>
              );
            })}
          </div>
          {!chosenReg && (
            <div style={{ fontSize:11.5, color:T.amber, marginTop:8 }}>
              ⚠️ Employee hasn't declared a tax regime — ask them to declare via Employee Portal.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function SalaryStructurePage() {
  const { T } = useTheme();
  const TYPE_C = { Earning:T.green, Deduction:T.red };
  const inp    = (err=false) => ({
    width:"100%", background:T.inp_bg,
    border:`1px solid ${err?T.red:T.border}`,
    borderRadius:8, color:T.text, fontSize:13,
    padding:"9px 12px", outline:"none",
    fontFamily:"Outfit,sans-serif", boxSizing:"border-box",
  });
  const lbl = { fontSize:11.5, color:T.muted, display:"block", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.06em" };

  const [structures,   setStructures]   = useState([]);
  const [assignments,  setAssignments]  = useState([]);
  const [employees,    setEmployees]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [expanded,     setExpanded]     = useState(null);

  // ── Structures — search & pagination ───────────────────────────
  const [structSearch, setStructSearch] = useState("");
  const [structPage,   setStructPage]   = useState(1);
  const STRUCT_PAGE_SIZE = 8;

  // ── Employee Salary Assignments — search & pagination ───────────
  const [assignSearch, setAssignSearch] = useState("");
  const [assignPage,   setAssignPage]   = useState(1);
  const ASSIGN_PAGE_SIZE = 20;

  // Structure modal
  const [sOpen,    setSOpen]    = useState(false);
  const [sMode,    setSMode]    = useState("add");
  const [sData,    setSData]    = useState({ name:"", description:"", is_active:true });
  const [sId,      setSId]      = useState(null);
  const [sDelOpen, setSDelOpen] = useState(false);
  const [sDelId,   setSDelId]   = useState(null);

  // Component modal
  const [cOpen,     setCOpen]     = useState(false);
  const [cMode,     setCMode]     = useState("add");
  const [cStructId, setCStructId] = useState(null);
  const [cData,     setCData]     = useState({ name:"", type:"Earning", calculation_type:"Percentage", value:0, percentage_of:"CTC", formula:"", is_taxable:true, sort_order:0, cap_amount:0, apply_if_below:0 });
  const [cId,       setCId]       = useState(null);
  const [cDelOpen,  setCDelOpen]  = useState(false);
  const [cDelId,    setCDelId]    = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Assign modal
  const [aOpen, setAOpen] = useState(false);
  const [aData, setAData] = useState({ employee_id:"", structure_id:"", ctc:"", effective_from:"" });
  const [aPreview, setAPreview] = useState(null);

  // Breakdown modal
  const [bdOpen,   setBdOpen]   = useState(false);
  const [bdData,   setBdData]   = useState(null);

  // Inline CTC edit
  const [editCtcId,  setEditCtcId]  = useState(null);
  const [editCtcVal, setEditCtcVal] = useState("");

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [sRes, aRes, eRes] = await Promise.all([
        axios.get(APIConstant.salaryStructures,             ax()),
        axios.get(APIConstant.salaryAssignments,            ax()),
        axios.get(`${APIConstant.employees}?status=Active`, ax()),
      ]);
      setStructures(sRes.data  || []);
      setAssignments(aRes.data || []);
      setEmployees(eRes.data   || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  // ── Structures — filter + paginate ──────────────────────────────
  const filteredStructures = useMemo(() => structures.filter(s => {
    const q = structSearch.toLowerCase();
    return !q || s.name.toLowerCase().includes(q) || (s.description||"").toLowerCase().includes(q);
  }), [structures, structSearch]);

  useEffect(() => { setStructPage(1); }, [structSearch]);
  const structTotalPages = Math.max(1, Math.ceil(filteredStructures.length / STRUCT_PAGE_SIZE));
  useEffect(() => { if (structPage > structTotalPages) setStructPage(structTotalPages); }, [structTotalPages, structPage]);
  const paginatedStructures = useMemo(() => {
    const start = (structPage - 1) * STRUCT_PAGE_SIZE;
    return filteredStructures.slice(start, start + STRUCT_PAGE_SIZE);
  }, [filteredStructures, structPage]);

  // ── Employee Salary Assignments — filter + paginate ─────────────
  const filteredAssignments = useMemo(() => assignments.filter(a => {
    const q = assignSearch.toLowerCase();
    return !q || `${a.employee_name} ${a.employee_code} ${a.department||""}`.toLowerCase().includes(q);
  }), [assignments, assignSearch]);

  useEffect(() => { setAssignPage(1); }, [assignSearch]);
  const assignTotalPages = Math.max(1, Math.ceil(filteredAssignments.length / ASSIGN_PAGE_SIZE));
  useEffect(() => { if (assignPage > assignTotalPages) setAssignPage(assignTotalPages); }, [assignTotalPages, assignPage]);
  const paginatedAssignments = useMemo(() => {
    const start = (assignPage - 1) * ASSIGN_PAGE_SIZE;
    return filteredAssignments.slice(start, start + ASSIGN_PAGE_SIZE);
  }, [filteredAssignments, assignPage]);

  // Preview salary breakdown before assigning
  const loadPreview = async () => {
    if (!aData.structure_id || !aData.ctc) { setAPreview(null); return; }
    try {
      const r = await axios.get(
        `${APIConstant.salaryStructures}preview?structure_id=${aData.structure_id}&ctc=${aData.ctc}`, ax()
      );
      setAPreview(r.data);
    } catch(e) { setAPreview(null); }
  };

  useEffect(() => { loadPreview(); }, [aData.structure_id, aData.ctc]);

  // View employee breakdown
  const viewBreakdown = async (employeeId) => {
    try {
      const r = await axios.get(`${APIConstant.salaryStructures}breakdown/${employeeId}`, ax());
      setBdData(r.data); setBdOpen(true);
    } catch(e) { alert(e.response?.data?.detail || "No salary assigned"); }
  };

  // Update CTC inline
  const saveCtc = async (esId) => {
    try {
      await axios.put(`${APIConstant.salaryStructures}assignments/${esId}`, { ctc: parseFloat(editCtcVal) }, ax());
      setEditCtcId(null); fetchAll();
    } catch(e) { alert(e.response?.data?.detail || "Error"); }
  };

  // Structure CRUD
  const saveStructure = async () => {
    try {
      if (sMode === "edit") await axios.put(`${APIConstant.salaryStructures}${sId}`, sData, ax());
      else                   await axios.post(APIConstant.salaryStructures, sData, ax());
      setSOpen(false); fetchAll();
    } catch(e) { console.error(e); }
  };

  const deleteStructure = async () => {
    try { await axios.delete(`${APIConstant.salaryStructures}${sDelId}`, ax()); setSDelOpen(false); fetchAll(); }
    catch(e) { alert(e.response?.data?.detail || "Cannot delete"); }
  };

  // Component CRUD
  const saveComponent = async () => {
    try {
      const payload = { ...cData, value: parseFloat(cData.value)||0,
        formula: cData.calculation_type === "Formula" ? cData.formula : null };
      if (cMode === "edit") await axios.put(`${APIConstant.salaryStructures}components/${cId}`, payload, ax());
      else                   await axios.post(`${APIConstant.salaryStructures}${cStructId}/components`, payload, ax());
      setCOpen(false); fetchAll();
    } catch(e) { console.error(e); }
  };

  const deleteComponent = async () => {
    try { await axios.delete(`${APIConstant.salaryStructures}components/${cDelId}`, ax()); setCDelOpen(false); fetchAll(); }
    catch(e) { console.error(e); }
  };

  // Assign
  const saveAssignment = async () => {
    if (!aData.employee_id || !aData.structure_id || !aData.ctc) return;
    try {
      await axios.post(APIConstant.salaryAssign, {
        employee_id:   parseInt(aData.employee_id),
        structure_id:  parseInt(aData.structure_id),
        ctc:           parseFloat(aData.ctc),
        effective_from:aData.effective_from || new Date().toISOString().split("T")[0],
      }, ax());
      setAOpen(false); setAPreview(null); fetchAll();
    } catch(e) { alert(e.response?.data?.detail || "Error assigning salary"); }
  };

  return (
    <div className={styles.pageRoot}>
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.badge}>COMPENSATION</div>
          <h2>Salary Structures</h2>
          <p>Define salary components and assign CTC to employees</p>
        </div>
        <div className={styles.headerRight}>
          <button className={styles.btnSecondary} onClick={() => { setAData({ employee_id:"", structure_id:"", ctc:"", effective_from:"" }); setAPreview(null); setAOpen(true); }}>
            <Users2 size={14}/> Assign to Employee
          </button>
          <button className={styles.btnPrimary} onClick={() => { setSMode("add"); setSData({ name:"", description:"", is_active:true }); setSOpen(true); }}>
            <Plus size={14}/> New Structure
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className={`${styles.statsGrid} ${styles.cols3}`} style={{ marginBottom:24 }}>
        <div className={`${styles.statCard} ${styles.cyan}`}><div className={styles.statVal}>{structures.length}</div><div className={styles.statLabel}>Structures</div></div>
        <div className={`${styles.statCard} ${styles.green}`}><div className={styles.statVal}>{assignments.length}</div><div className={styles.statLabel}>Employees Assigned</div></div>
        <div className={`${styles.statCard} ${styles.amber}`}><div className={styles.statVal}>{employees.length - assignments.length}</div><div className={styles.statLabel}>Without Salary</div></div>
      </div>

      {/* Structures — search bar */}
      {!loading && structures.length > 0 && (
        <div style={{ marginBottom:14, position:"relative", maxWidth:340 }}>
          <Search size={13} style={{ position:"absolute", left:11, top:"50%",
            transform:"translateY(-50%)", color:T.dim }}/>
          <input value={structSearch} onChange={e => setStructSearch(e.target.value)}
            placeholder="Search structures…"
            style={{ width:"100%", background:T.inp_bg, border:`1px solid ${T.border}`,
              borderRadius:8, color:T.text, fontSize:13, padding:"8px 12px 8px 32px",
              outline:"none", fontFamily:"Outfit,sans-serif", boxSizing:"border-box" }}/>
        </div>
      )}

      {/* Structures */}
      {loading ? <div className={styles.loader}><div className={styles.spinner}/>Loading…</div> : (
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
          {paginatedStructures.map(s => (
            <div key={s.id} className={styles.card}>
              <div style={{ padding:"16px 22px", display:"flex", alignItems:"center", gap:14, cursor:"pointer", borderBottom:expanded===s.id?`1px solid ${T.border}`:"none" }}
                onClick={() => setExpanded(expanded===s.id?null:s.id)}>
                <div style={{ width:40, height:40, borderRadius:10, background:`${T.cyan}1A`, display:"flex", alignItems:"center", justifyContent:"center", color:T.cyan, flexShrink:0 }}>
                  <Layers size={18}/>
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:15, fontWeight:600, color:T.text }}>{s.name}</div>
                  <div style={{ fontSize:12, color:T.muted, marginTop:2 }}>{s.description||"—"} · {s.components?.length||0} components · {s.employee_count} employees</div>
                </div>
                <span style={{ fontSize:11, padding:"3px 9px", borderRadius:20, background:s.is_active?`${T.green}1F`:`${T.red}1F`, color:s.is_active?T.green:T.red }}>
                  {s.is_active?"Active":"Inactive"}
                </span>
                <div style={{ display:"flex", gap:6 }} onClick={e=>e.stopPropagation()}>
                  <button className={styles.btnSm} onClick={() => { setSMode("edit"); setSId(s.id); setSData({ name:s.name, description:s.description||"", is_active:s.is_active }); setSOpen(true); }}>
                    <Edit2 size={12}/>
                  </button>
                  <button className={styles.btnSm} style={{ color:T.green, borderColor:`${T.green}40` }}
                    onClick={() => { setCMode("add"); setCStructId(s.id); setCData({ name:"", type:"Earning", calculation_type:"Percentage", value:0, percentage_of:"CTC", formula:"", is_taxable:true, sort_order:(s.components?.length||0)+1 }); setCOpen(true); }}>
                    <Plus size={12}/> Component
                  </button>
                  <button className={styles.btnSm} style={{ color:T.red, borderColor:`${T.red}40` }} onClick={() => { setSDelId(s.id); setSDelOpen(true); }}>
                    <Trash2 size={12}/>
                  </button>
                </div>
                {expanded===s.id ? <ChevronUp size={15} style={{ color:T.muted }}/> : <ChevronDown size={15} style={{ color:T.muted }}/>}
              </div>

              {expanded===s.id && (
                <div style={{ padding:"16px 22px" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse" }}>
                    <thead>
                      <tr>{["Component","Type","Calculation","Value / Formula","Taxable","Actions"].map(h=>(
                        <th key={h} style={{ fontSize:10.5, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.1em", color:T.dim, padding:"0 10px 10px", textAlign:"left", borderBottom:`1px solid ${T.cyan}0F` }}>{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody>
                      {(s.components||[]).sort((a,b)=>a.sort_order-b.sort_order).map(c => (
                        <tr key={c.id}>
                          <td style={{ padding:"11px 10px", fontSize:13, fontWeight:500, color:T.text, borderBottom:`1px solid ${T.cyan}0D` }}>{c.name}</td>
                          <td style={{ padding:"11px 10px", borderBottom:`1px solid ${T.cyan}0D` }}>
                            <span style={{ fontSize:11.5, padding:"3px 9px", borderRadius:20, background:`${TYPE_C[c.type]}20`, color:TYPE_C[c.type], fontWeight:600 }}>{c.type}</span>
                          </td>
                          <td style={{ padding:"11px 10px", fontSize:12.5, color:T.muted, borderBottom:`1px solid ${T.cyan}0D` }}>{c.calculation_type}</td>
                          <td style={{ padding:"11px 10px", fontFamily:"JetBrains Mono,monospace", fontSize:12, color:T.cyan, borderBottom:`1px solid ${T.cyan}0D` }}>
                            {c.calculation_type === "Formula"
                              ? <span style={{ color:T.purple }}>{c.formula}</span>
                              : c.calculation_type === "Percentage"
                                ? `${c.value}% of ${c.percentage_of}`
                                : `₹${c.value.toLocaleString("en-IN")}`}
                          </td>
                          <td style={{ padding:"11px 10px", borderBottom:`1px solid ${T.cyan}0D` }}>
                            <span style={{ fontSize:11, padding:"2px 8px", borderRadius:10, background:c.is_taxable?`${T.red}1A`:`${T.green}1A`, color:c.is_taxable?T.red:T.green }}>
                              {c.is_taxable?"Taxable":"Non-taxable"}
                            </span>
                          </td>
                          <td style={{ padding:"11px 10px", borderBottom:`1px solid ${T.cyan}0D` }}>
                            <div style={{ display:"flex", gap:5 }}>
                              <button className={styles.btnSm} onClick={() => { setCMode("edit"); setCId(c.id); setShowAdvanced(!!(c.cap_amount||c.apply_if_below)); setCData({ name:c.name, type:c.type, calculation_type:c.calculation_type, value:c.value, percentage_of:c.percentage_of, formula:c.formula||"", cap_amount:c.cap_amount||0, apply_if_below:c.apply_if_below||0, is_taxable:c.is_taxable, sort_order:c.sort_order }); setCOpen(true); }}>
                                <Edit2 size={11}/>
                              </button>
                              <button className={styles.btnSm} style={{ color:T.red, borderColor:`${T.red}40` }} onClick={() => { setCDelId(c.id); setCDelOpen(true); }}>
                                <Trash2 size={11}/>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {(!s.components||s.components.length===0) && (
                        <tr><td colSpan={6} style={{ textAlign:"center", padding:20, color:T.muted, fontSize:13 }}>No components — click "+ Component"</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
          {filteredStructures.length===0 && (
            <div style={{ textAlign:"center", padding:60, color:T.muted, fontSize:14 }}>
              {structures.length === 0 ? 'No salary structures — click "New Structure"' : "No structures match your search"}
            </div>
          )}
          <PaginationBar
            page={structPage}
            totalPages={structTotalPages}
            onChange={setStructPage}
            totalItems={filteredStructures.length}
            pageSize={STRUCT_PAGE_SIZE}
            T={T}
          />
        </div>
      )}

      {/* Employee Assignments */}
      {assignments.length > 0 && (
        <div className={styles.card} style={{ marginTop:20 }}>
          <div className={styles.cardHeader}>
            <h3>Employee Salary Assignments</h3>
            <span className={styles.cardMeta}>{filteredAssignments.length} employees</span>
          </div>
          <div style={{ padding:"12px 22px", borderBottom:`1px solid ${T.border}` }}>
            <div style={{ position:"relative", maxWidth:300 }}>
              <Search size={13} style={{ position:"absolute", left:10, top:"50%",
                transform:"translateY(-50%)", color:T.dim }}/>
              <input value={assignSearch} onChange={e => setAssignSearch(e.target.value)}
                placeholder="Search employee…"
                style={{ width:"100%", background:T.inp_bg, border:`1px solid ${T.border}`,
                  borderRadius:8, color:T.text, fontSize:13, padding:"7px 12px 7px 30px",
                  outline:"none", fontFamily:"Outfit,sans-serif", boxSizing:"border-box" }}/>
            </div>
          </div>
          <div className={styles.tableWrap}>
            <table className={styles.darkTable}>
              <thead><tr><th>Employee</th><th>Department</th><th>Structure</th><th>Annual CTC</th><th>Monthly</th><th>Effective</th><th>Actions</th></tr></thead>
              <tbody>
                {filteredAssignments.length === 0 ? (
                  <tr className={styles.emptyRow}><td colSpan={7}>No employees found</td></tr>
                ) : paginatedAssignments.map(a => (
                  <tr key={a.id}>
                    <td><div style={{ fontWeight:500 }}>{a.employee_name}</div><div style={{ fontSize:11, color:T.muted }}>{a.employee_code}</div></td>
                    <td className={styles.dim}>{a.department||"—"}</td>
                    <td><span style={{ fontSize:12, padding:"3px 10px", borderRadius:20, background:`${T.cyan}1A`, color:T.cyan }}>{a.structure_name}</span></td>
                    <td>
                      {editCtcId === a.id ? (
                        <div style={{ display:"flex", gap:5, alignItems:"center" }}>
                          <input type="number" value={editCtcVal}
                            onChange={e=>setEditCtcVal(e.target.value)}
                            style={{ width:110, background:T.inp_bg, border:`1px solid ${T.cyan}26`, borderRadius:7, color:T.text, fontSize:12, padding:"4px 8px", outline:"none", fontFamily:"Outfit,sans-serif" }}/>
                          <button onClick={()=>saveCtc(a.id)} style={{ background:"none", border:"none", cursor:"pointer", color:T.green }}><Save size={13}/></button>
                          <button onClick={()=>setEditCtcId(null)} style={{ background:"none", border:"none", cursor:"pointer", color:T.muted }}><X size={13}/></button>
                        </div>
                      ) : (
                        <span style={{ fontFamily:"JetBrains Mono,monospace", color:T.amber, fontWeight:600, cursor:"pointer" }}
                          onClick={() => { setEditCtcId(a.id); setEditCtcVal(a.ctc); }}
                          title="Click to edit">
                          ₹{(a.ctc/100000).toFixed(2)}L ✎
                        </span>
                      )}
                    </td>
                    <td><span style={{ fontFamily:"JetBrains Mono,monospace", color:T.green }}>₹{Math.round(a.monthly_ctc).toLocaleString("en-IN")}</span></td>
                    <td className={styles.dim}>{a.effective_from||"—"}</td>
                    <td>
                      <button className={styles.btnSm} style={{ color:T.cyan, borderColor:`${T.cyan}33` }}
                        onClick={() => viewBreakdown(a.employee_id)}>
                        <Eye size={12}/> Breakdown
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationBar
            page={assignPage}
            totalPages={assignTotalPages}
            onChange={setAssignPage}
            totalItems={filteredAssignments.length}
            pageSize={ASSIGN_PAGE_SIZE}
            T={T}
          />
        </div>
      )}

      {/* ── Modals ── */}

      {/* Structure */}
      <Dialog open={sOpen} onClose={()=>setSOpen(false)}>
        <div style={{ background:T.card, border:`1px solid ${T.cyan}33`, borderRadius:14, width:460, fontFamily:"Outfit,sans-serif", overflow:"hidden" }}>
          <div style={{ padding:"18px 24px", borderBottom:`1px solid ${T.border}`, background:`${T.cyan}0A`, display:"flex", justifyContent:"space-between" }}>
            <h3 style={{ margin:0, fontSize:16, fontWeight:600, color:T.text }}>{sMode==="add"?"New":"Edit"} Salary Structure</h3>
            <button style={{ background:"none", border:"none", cursor:"pointer", color:T.muted }} onClick={()=>setSOpen(false)}><X size={16}/></button>
          </div>
          <div style={{ padding:24, display:"flex", flexDirection:"column", gap:14 }}>
            <div><label style={lbl}>Structure Name *</label><input value={sData.name} onChange={e=>setSData({...sData,name:e.target.value})} placeholder="e.g. Engineering Senior" style={inp(!sData.name)}/></div>
            <div><label style={lbl}>Description</label><textarea value={sData.description} onChange={e=>setSData({...sData,description:e.target.value})} rows={2} style={{...inp(),resize:"vertical"}}/></div>
            <div style={{ display:"flex", gap:10, justifyContent:"flex-end", paddingTop:8, borderTop:`1px solid ${T.border}` }}>
              <button onClick={()=>setSOpen(false)} className={styles.btnSecondary}>Cancel</button>
              <button onClick={saveStructure} className={styles.btnPrimary}>{sMode==="add"?"Create":"Update"}</button>
            </div>
          </div>
        </div>
      </Dialog>

      {/* Component */}
      <Dialog open={cOpen} onClose={()=>setCOpen(false)}>
        <div style={{ background:T.card, border:`1px solid ${T.cyan}33`, borderRadius:14, width:500, fontFamily:"Outfit,sans-serif", overflow:"hidden" }}>
          <div style={{ padding:"18px 24px", borderBottom:`1px solid ${T.border}`, background:`${T.cyan}0A`, display:"flex", justifyContent:"space-between" }}>
            <h3 style={{ margin:0, fontSize:16, fontWeight:600, color:T.text }}>{cMode==="add"?"Add":"Edit"} Salary Component</h3>
            <button style={{ background:"none", border:"none", cursor:"pointer", color:T.muted }} onClick={()=>setCOpen(false)}><X size={16}/></button>
          </div>
          <div style={{ padding:24, display:"flex", flexDirection:"column", gap:14 }}>
            <div><label style={lbl}>Component Name *</label><input value={cData.name} onChange={e=>setCData({...cData,name:e.target.value})} placeholder="e.g. Basic, HRA, Other Allowance" style={inp(!cData.name)}/></div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              <div><label style={lbl}>Type</label>
                <select value={cData.type} onChange={e=>setCData({...cData,type:e.target.value})} style={inp()}>
                  <option>Earning</option><option>Deduction</option>
                </select>
              </div>
              <div><label style={lbl}>Calculation</label>
                <select value={cData.calculation_type} onChange={e=>setCData({...cData,calculation_type:e.target.value})} style={inp()}>
                  <option value="Percentage">Percentage (%)</option>
                  <option value="Fixed">Fixed Amount (₹)</option>
                  <option value="Formula">Formula</option>
                </select>
              </div>
            </div>

            {/* Formula input */}
            {cData.calculation_type === "Formula" ? (
              <div>
                <label style={lbl}>Formula</label>
                <input value={cData.formula} onChange={e=>setCData({...cData,formula:e.target.value})}
                  placeholder="e.g. Gross-(Basic+HRA)" style={inp(!cData.formula)}/>
                <div style={{ marginTop:6, fontSize:11.5, color:T.muted, lineHeight:1.6 }}>
                  Available variables: <span style={{ color:T.purple }}>CTC, MonthlyCTC, Gross, Basic, HRA</span> and any component name (computed in sort order).
                  <br/>Example: <span style={{ color:T.green, fontFamily:"JetBrains Mono,monospace" }}>Gross-(Basic+HRA)</span>
                </div>
              </div>
            ) : (
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                <div><label style={lbl}>{cData.calculation_type==="Percentage"?"Percentage (%)":"Amount (₹)"}</label>
                  <input type="number" min={0} value={cData.value} onChange={e=>setCData({...cData,value:e.target.value})} style={inp()}/>
                </div>
                {cData.calculation_type === "Percentage" && (
                  <div><label style={lbl}>% Of</label>
                    <select value={cData.percentage_of} onChange={e=>setCData({...cData,percentage_of:e.target.value})} style={inp()}>
                      <option value="CTC">CTC (Monthly)</option>
                      <option value="Basic">Basic</option>
                      <option value="Gross">Gross</option>
                    </select>
                  </div>
                )}
              </div>
            )}

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              <div><label style={lbl}>Taxable?</label>
                <select value={String(cData.is_taxable)} onChange={e=>setCData({...cData,is_taxable:e.target.value==="true"})} style={inp()}>
                  <option value="true">Taxable</option><option value="false">Non-taxable</option>
                </select>
              </div>
              <div><label style={lbl}>Sort Order</label>
                <input type="number" min={0} value={cData.sort_order} onChange={e=>setCData({...cData,sort_order:parseInt(e.target.value)||0})} style={inp()}/>
              </div>
            </div>
            {cData.type === "Deduction" && (
              <div>
                <button type="button" onClick={() => setShowAdvanced(s => !s)}
                  style={{ background:"none", border:"none", cursor:"pointer", color:T.muted, fontSize:12, fontFamily:"Outfit,sans-serif", display:"flex", alignItems:"center", gap:5, padding:0 }}>
                  <span>{showAdvanced ? "▾" : "▸"}</span>
                  Advanced deduction rules
                  {(cData.cap_amount > 0 || cData.apply_if_below > 0) && (
                    <span style={{ fontSize:10, padding:"1px 6px", borderRadius:8, background:`${T.purple}26`, color:T.purple, marginLeft:4 }}>configured</span>
                  )}
                </button>
                {showAdvanced && (
                  <div style={{ marginTop:10, display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, padding:14, background:`${T.purple}0A`, border:`1px solid ${T.purple}1F`, borderRadius:9 }}>
                    <div>
                      <label style={lbl}>Cap Amount (₹) — max limit</label>
                      <input type="number" min={0} value={cData.cap_amount}
                        onChange={e => setCData({...cData, cap_amount: parseFloat(e.target.value)||0})}
                        placeholder="e.g. 1800 for PF" style={inp()}/>
                      {cData.cap_amount > 0 && <div style={{fontSize:11,color:T.amber,marginTop:3}}>Max ₹{Number(cData.cap_amount).toLocaleString("en-IN")}/month</div>}
                    </div>
                    <div>
                      <label style={lbl}>Skip if Gross above (₹)</label>
                      <input type="number" min={0} value={cData.apply_if_below}
                        onChange={e => setCData({...cData, apply_if_below: parseFloat(e.target.value)||0})}
                        placeholder="e.g. 21000 for ESI" style={inp()}/>
                      {cData.apply_if_below > 0 && <div style={{fontSize:11,color:T.cyan,marginTop:3}}>Only if Gross ≤ ₹{Number(cData.apply_if_below).toLocaleString("en-IN")}</div>}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Preview */}
            <div style={{ background:`${T.cyan}0D`, border:`1px solid ${T.cyan}1A`, borderRadius:8, padding:"10px 14px", fontSize:13, color:T.muted }}>
              Preview: <strong style={{ color:T.text }}>{cData.name||"Component"}</strong> →{" "}
              <span style={{ color:TYPE_C[cData.type] }}>{cData.type}</span> ·{" "}
              {cData.calculation_type==="Formula"
                ? <span style={{ color:T.purple, fontFamily:"JetBrains Mono,monospace" }}>{cData.formula||"formula"}</span>
                : cData.calculation_type==="Percentage"
                  ? <span style={{ color:T.cyan }}>{cData.value}% of {cData.percentage_of}</span>
                  : <span style={{ color:T.amber }}>₹{Number(cData.value).toLocaleString("en-IN")}/month</span>}
            </div>

            <div style={{ display:"flex", gap:10, justifyContent:"flex-end", paddingTop:8, borderTop:`1px solid ${T.border}` }}>
              <button onClick={()=>setCOpen(false)} className={styles.btnSecondary}>Cancel</button>
              <button onClick={saveComponent} className={styles.btnPrimary}>{cMode==="add"?"Add":"Update"}</button>
            </div>
          </div>
        </div>
      </Dialog>

      {/* Assign Salary */}
      <Dialog open={aOpen} onClose={()=>{ setAOpen(false); setAPreview(null); }} maxWidth="md">
        <div style={{ background:T.card, border:`1px solid ${T.cyan}33`, borderRadius:14, width: aPreview ? 900 : 460, fontFamily:"Outfit,sans-serif", overflow:"hidden", display:"flex" }}>
          {/* Left — form */}
          <div style={{ flex:"0 0 440px", borderRight: aPreview ? `1px solid ${T.border}` : "none" }}>
            <div style={{ padding:"18px 24px", borderBottom:`1px solid ${T.border}`, background:`${T.cyan}0A`, display:"flex", justifyContent:"space-between" }}>
              <h3 style={{ margin:0, fontSize:16, fontWeight:600, color:T.text }}>Assign Salary to Employee</h3>
              <button style={{ background:"none", border:"none", cursor:"pointer", color:T.muted }} onClick={()=>{ setAOpen(false); setAPreview(null); }}><X size={16}/></button>
            </div>
            <div style={{ padding:24, display:"flex", flexDirection:"column", gap:14 }}>
              <div><label style={lbl}>Employee *</label>
                <select value={aData.employee_id} onChange={e=>setAData({...aData,employee_id:e.target.value})} style={inp(!aData.employee_id)}>
                  <option value="">Select employee</option>
                  {employees.map(e=><option key={e.id} value={e.id}>{e.first_name} {e.last_name} ({e.employee_code})</option>)}
                </select>
              </div>
              <div><label style={lbl}>Salary Structure *</label>
                <select value={aData.structure_id} onChange={e=>setAData({...aData,structure_id:e.target.value})} style={inp(!aData.structure_id)}>
                  <option value="">Select structure</option>
                  {structures.filter(s=>s.is_active).map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div><label style={lbl}>Annual CTC (₹) *</label>
                <input type="number" min={0} value={aData.ctc} onChange={e=>setAData({...aData,ctc:e.target.value})} placeholder="e.g. 1200000" style={inp(!aData.ctc)}/>
                {aData.ctc && <div style={{ fontSize:12, color:T.cyan, marginTop:5 }}>Monthly: ₹{Math.round(aData.ctc/12).toLocaleString("en-IN")}</div>}
              </div>
              <div><label style={lbl}>Effective From</label>
                <input type="date" value={aData.effective_from} onChange={e=>setAData({...aData,effective_from:e.target.value})} style={inp()}/>
              </div>
              <div style={{ display:"flex", gap:10, justifyContent:"flex-end", paddingTop:8, borderTop:`1px solid ${T.border}` }}>
                <button onClick={()=>{ setAOpen(false); setAPreview(null); }} className={styles.btnSecondary}>Cancel</button>
                <button onClick={saveAssignment} className={styles.btnPrimary}><DollarSign size={13}/> Assign Salary</button>
              </div>
            </div>
          </div>

          {/* Right — live preview */}
          {aPreview && (
            <div style={{ flex:1, padding:0 }}>
              <BreakdownTable data={{ ...aPreview, structure_name: structures.find(s=>String(s.id)===String(aData.structure_id))?.name }} />
            </div>
          )}
        </div>
      </Dialog>

      {/* Breakdown modal */}
      <Dialog open={bdOpen} onClose={()=>setBdOpen(false)} maxWidth="sm">
        <BreakdownTable data={bdData} onClose={()=>setBdOpen(false)}/>
      </Dialog>

      <Popup open={sDelOpen} message="Delete this structure?" handleClose={()=>setSDelOpen(false)} handleConfirm={deleteStructure}/>
      <Popup open={cDelOpen} message="Delete this component?" handleClose={()=>setCDelOpen(false)} handleConfirm={deleteComponent}/>
    </div>
  );
}